"use client"

import React, { useMemo } from "react"
import { CheckCircle, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"

interface DocumentData {
  書類名: string
  届出先: string
  届出者: string
  届出時期: string
  補足事項: string
  タイプ: string
}

interface QuizQuestionProps {
  documentData: DocumentData
  allDocs: DocumentData[]

  isRevealed: boolean

  selectedSaki: string
  setSelectedSaki: (val: string) => void

  selectedSha: string
  setSelectedSha: (val: string) => void

  selectedJiki: string
  setSelectedJiki: (val: string) => void
}

/**
 * 1問のUI
 */
export function QuizQuestion({
  documentData,
  allDocs,
  isRevealed,
  selectedSaki,
  setSelectedSaki,
  selectedSha,
  setSelectedSha,
  selectedJiki,
  setSelectedJiki,
}: QuizQuestionProps) {
  // 同タイプフィルタ
  const sameTypeDocs = useMemo(
    () => allDocs.filter((doc) => doc.タイプ === documentData.タイプ),
    [documentData, allDocs],
  )

  // 2択生成
  const [sakiChoices, shaChoices, jikiChoices] = useMemo(() => {
    // 正解
    const correctSaki = documentData.届出先
    const correctSha = documentData.届出者
    const correctJiki = documentData.届出時期

    // それぞれのフィールドについて
    const saki2 = buildTwoChoices(sameTypeDocs, "届出先", correctSaki)
    const sha2 = buildTwoChoices(sameTypeDocs, "届出者", correctSha)
    const jiki2 = buildTwoChoices(sameTypeDocs, "届出時期", correctJiki)

    return [saki2, sha2, jiki2]
  }, [documentData, sameTypeDocs])

  const isSakiCorrect = selectedSaki === documentData.届出先
  const isShaCorrect = selectedSha === documentData.届出者
  const isJikiCorrect = selectedJiki === documentData.届出時期

  return (
    <div>
      <div className="mb-4">
        <p className="font-bold text-lg">書類名：{documentData.書類名}</p>
      </div>

      {/* 届出先 */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <p className="font-semibold">届出先を選択</p>
          {isRevealed &&
            (isSakiCorrect ? (
              <div className="flex items-center gap-1 ml-3 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span>正解</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 ml-3 text-red-600">
                <XCircle className="w-5 h-5" />
                <span>不正解</span>
              </div>
            ))}
        </div>
        <div className="flex gap-2 mt-1">
          {sakiChoices.map((choice) => (
            <Button
              key={choice}
              variant={choice === selectedSaki ? "default" : "outline"}
              onClick={() => {
                if (!isRevealed) {
                  setSelectedSaki(choice)
                }
              }}
            >
              {choice}
            </Button>
          ))}
        </div>
      </div>

      {/* 届出者 */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <p className="font-semibold">届出者を選択</p>
          {isRevealed &&
            (isShaCorrect ? (
              <div className="flex items-center gap-1 ml-3 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span>正解</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 ml-3 text-red-600">
                <XCircle className="w-5 h-5" />
                <span>不正解</span>
              </div>
            ))}
        </div>
        <div className="flex gap-2 mt-1">
          {shaChoices.map((choice) => (
            <Button
              key={choice}
              variant={choice === selectedSha ? "default" : "outline"}
              onClick={() => {
                if (!isRevealed) {
                  setSelectedSha(choice)
                }
              }}
            >
              {choice}
            </Button>
          ))}
        </div>
      </div>

      {/* 届出時期 */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <p className="font-semibold">届出時期を選択</p>
          {isRevealed &&
            (isJikiCorrect ? (
              <div className="flex items-center gap-1 ml-3 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span>正解</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 ml-3 text-red-600">
                <XCircle className="w-5 h-5" />
                <span>不正解</span>
              </div>
            ))}
        </div>
        <div className="flex gap-2 mt-1">
          {jikiChoices.map((choice) => (
            <Button
              key={choice}
              variant={choice === selectedJiki ? "default" : "outline"}
              onClick={() => {
                if (!isRevealed) {
                  setSelectedJiki(choice)
                }
              }}
            >
              {choice}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * 同タイプの中から "field"(届出先/届出者/届出時期)を収集し
 * 正解値 + その中のランダム1つ(ただし正解と重複しない)を2択化する
 */
function buildTwoChoices(
  docs: DocumentData[],
  field: "届出先" | "届出者" | "届出時期",
  correctValue: string,
): string[] {
  // 1. 全ての docs から field 値を集める (同タイプ)
  const allValues = Array.from(new Set(docs.map((d) => d[field])))

  // 2. 正解を除いたリスト
  const candidates = allValues.filter((v) => v !== correctValue)

  // 3. ランダムに1つ
  // データを見る限り必ず少なくとも1つは存在するはず
  // ただし万一候補が無ければ fallback
  if (candidates.length === 0) {
    // fallback(正解しか存在しない場合)
    return [correctValue, correctValue + "他"]
  }
  const randomIndex = Math.floor(Math.random() * candidates.length)
  const wrong = candidates[randomIndex]

  // 4. 2択
  const result = [correctValue, wrong]
  shuffleArray(result)
  return result
}

/** シャッフル */
function shuffleArray<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const r = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[r]] = [arr[r], arr[i]]
  }
}
